import type { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { handleDeviceDetect } from 'src/provider/ChannelContextProvider'
import { sanitizeSentryPage, sanitizeSentryUrl } from 'src/utils/sentry/sanitizeSentryUrl'

/**
 * Sentry 공통 설정·필터·capture 유틸
 *
 * index.tsx에서 beforeSend / beforeBreadcrumb 등록.
 * captureError·captureMessage는 페이지·API·Bridge 모듈에서 공통 사용.
 */

export const SENTRY_SEVERITY = {
  Info: 'info',
  Warning: 'warning',
  Error: 'error',
} as const

/** Sentry에서 완전히 버릴 에러 메시지 패턴 */
const IGNORED_ERROR_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'Object captured as promise rejection',
  'ERR_NETWORK',
  'Network Error',
  'Load failed', // iOS Safari 네트워크 실패
  'Failed to fetch', // Chrome fetch 실패
  'NetworkError when attempting to fetch resource', // Firefox 네트워크 실패
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'Importing a module script failed',
  'Failed to update a ServiceWorker',
  'Failed to register a ServiceWorker',
]

// ─────────────────────────────────────────────
// 공통 유틸
// ─────────────────────────────────────────────

/** 오프라인 상태에서 발생한 네트워크 에러인지 확인 */
const isOfflineError = (hint?: EventHint): boolean => {
  const err = hint?.originalException
  if (!err || typeof err !== 'object') return false
  return !navigator.onLine || ('code' in err && (err as any).code === 'ERR_NETWORK')
}

/** 인앱·광고 WebView 노이즈 */
const isAnonymousFrame = (filename?: string | null) =>
  !filename ||
  filename === '<anonymous>' ||
  filename.includes('[native code]') ||
  filename.startsWith('native ')

const isInAppWebViewNoise = (event: ErrorEvent): boolean => {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
  if (frames.length === 0) return false
  return frames.every((f) => isAnonymousFrame(f.filename))
}

/** 무시할 에러 메시지인지 확인 */
const MEANINGLESS_MESSAGE_MAX_LENGTH = 3
const MEANINGLESS_MESSAGE_MAX_FRAMES = 1

const isIgnoredError = (event: ErrorEvent, hint?: EventHint): boolean => {
  const exception = event.exception?.values?.[0]
  const message =
    exception?.value ??
    event.message ??
    (hint?.originalException instanceof Error
      ? hint.originalException.message
      : String(hint?.originalException ?? ''))
  if (!message) return false
  // 알려진 노이즈 문구
  if (IGNORED_ERROR_MESSAGES.some((pattern) => message.includes(pattern))) {
    return true
  }
  // 추적 불가능한 초단문
  const frames = exception?.stacktrace?.frames ?? []
  if (
    message.trim().length <= MEANINGLESS_MESSAGE_MAX_LENGTH &&
    frames.length <= MEANINGLESS_MESSAGE_MAX_FRAMES
  ) {
    return true
  }
  return false
}

// ─────────────────────────────────────────────
// beforeSend
// ─────────────────────────────────────────────

export const beforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  /**
   * Sentry로 이벤트가 전송되기 직전에 호출되는 필터/정제 함수
   * null을 반환하면 해당 이벤트는 Sentry로 전송되지 않음
   */

  // 무의미한 네트워크 에러 (사용자 인터넷 끊김)
  if (isOfflineError(hint)) return null

  // 노이즈 에러 메시지 버림
  if (isIgnoredError(event, hint)) return null

  // 인앱·광고 WebView 주입 스크립트 노이즈
  if (isInAppWebViewNoise(event)) return null

  if (event.request?.url) {
    event.request.url = sanitizeSentryUrl(event.request.url)
  }

  const browser = event.contexts?.browser
  if (browser && typeof browser === 'object' && typeof browser.url === 'string') {
    browser.url = sanitizeSentryUrl(browser.url)
  }

  return event
}

// ─────────────────────────────────────────────
// beforeBreadcrumb
// ─────────────────────────────────────────────

export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint,
): Breadcrumb | null => {
  // console.log 제거
  if (breadcrumb.category === 'console') {
    return breadcrumb.level === SENTRY_SEVERITY.Error ? breadcrumb : null
  }
  // 성능 추적 breadcrumb 제거
  if (['sentry.transaction', 'sentry.performance'].includes(breadcrumb.category ?? '')) {
    return null
  }

  // navigation 민감 동적 세그먼트 마스킹 처리
  if (breadcrumb.category === 'navigation') {
    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        from: breadcrumb.data?.from
          ? sanitizeSentryPage(new URL(breadcrumb.data.from, window.location.origin).pathname)
          : undefined,
        to: breadcrumb.data?.to
          ? sanitizeSentryPage(new URL(breadcrumb.data.to, window.location.origin).pathname)
          : undefined,
      },
    }
  }
  return breadcrumb
}

// ─────────────────────────────────────────────
// captureError
// ─────────────────────────────────────────────

/**
 * Sentry Scope에 공통 태그/extra를 적용
 * page 태그는 항상 현재 경로(마스킹)로 자동 주입
 */
const applyScopeMeta = (
  scope: Sentry.Scope,
  tags?: Record<string, string>,
  extra?: Record<string, unknown>,
) => {
  const { channel } = handleDeviceDetect()

  scope.setTag('page', tags?.page ?? sanitizeSentryPage())
  scope.setTag('channel', tags?.channel ?? channel)

  Object.entries(tags ?? {}).forEach(([k, v]) => {
    scope.setTag(k, v)
  })

  if (extra) {
    scope.setExtras(extra)
  }
}

interface CaptureErrorOptions {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string>
  extra?: Record<string, unknown>
}

export const captureError = (error: unknown, options: CaptureErrorOptions = {}) => {
  const { level = SENTRY_SEVERITY.Error, tags, extra } = options

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeMeta(scope, tags, extra)

    Sentry.captureException(error instanceof Error ? error : new Error(String(error)))
  })
}

// ─────────────────────────────────────────────
// captureMessage
// ─────────────────────────────────────────────

interface CaptureMessageOptions {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string>
  extra?: Record<string, unknown>
}

export const captureMessage = (message: string, options: CaptureMessageOptions = {}) => {
  const { level = SENTRY_SEVERITY.Info, tags = {}, extra } = options

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeMeta(scope, tags, extra)

    Sentry.captureMessage(message, level)
  })
}

// ─────────────────────────────────────────────
// Error 타입 헬퍼
// ─────────────────────────────────────────────

export const toBridgeError = (error: Error): Error => {
  error.name = 'BridgeError'
  return error
}

export const toApiError = (error: Error): Error => {
  error.name = 'ApiError'
  return error
}
